import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { FunnelElement } from '../../types/element.types';
import { useFunnelStore } from '../../store/funnelStore';
import { getResponsiveStyles } from '../../utils/helpers';

// Import element renderers
import HeadingElement from '../Elements/HeadingElement';
import TextElement from '../Elements/TextElement';
import ImageElement from '../Elements/ImageElement';
import ButtonElement from '../Elements/ButtonElement';
import InputElement from '../Elements/InputElement';
import TextareaElement from '../Elements/TextareaElement';
import CheckboxElement from '../Elements/CheckboxElement';
import SelectElement from '../Elements/SelectElement';
import SpacerElement from '../Elements/SpacerElement';
import DividerElement from '../Elements/DividerElement';
import BulletedListElement from '../Elements/BulletedListElement';
import VideoElement from '../Elements/VideoElement';
import IconElement from '../Elements/IconElement';
import CarouselElement from '../Elements/CarouselElement';
import AccordionElement from '../Elements/AccordionElement';
import TabsElement from '../Elements/TabsElement';
import ContentBoxElement from '../Elements/ContentBoxElement';
import CustomHtmlElement from '../Elements/CustomHtmlElement';
import ColumnLayoutElement from '../Elements/ColumnLayoutElement';

interface CanvasElementProps {
  element: FunnelElement;
  sectionId: string;
  columnId: string;
}

/**
 * Canvas Element Component
 * Renders an element on the canvas with appropriate styles and interactions
 */
const CanvasElement: React.FC<CanvasElementProps> = ({ element, sectionId, columnId }) => {
  const { selectedElementId, selectElement, currentBreakpoint, isPreviewMode } = useFunnelStore();
  const isSelected = selectedElementId === element.id;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: element.id,
    data: { type: 'element', element, sectionId, columnId },
  });

  // Get responsive styles based on current breakpoint
  const styles = getResponsiveStyles(element.styles, element.responsive, currentBreakpoint);

  const handleClick = (e: React.MouseEvent) => {
    // Don't handle clicks in preview mode
    if (isPreviewMode) return;
    
    e.preventDefault();
    e.stopPropagation();
    // Don't select if we're dragging
    if (!isDragging) {
      console.log('Element clicked:', element.id, element.type);
      selectElement(element.id);
    }
  };

  // Render the appropriate element component based on type
  const renderElement = () => {
    switch (element.type) {
      case 'heading':
        return <HeadingElement element={element} />;
      case 'text':
        return <TextElement element={element} />;
      case 'bulletedList':
        return <BulletedListElement element={element} />;
      case 'image':
        return <ImageElement element={element} />;
      case 'video':
        return <VideoElement element={element} />;
      case 'button':
        return <ButtonElement element={element} />;
      case 'input':
        return <InputElement element={element} />;
      case 'textarea':
        return <TextareaElement element={element} />;
      case 'checkbox':
        return <CheckboxElement element={element} />;
      case 'select':
        return <SelectElement element={element} />;
      case 'columnLayout':
        return <ColumnLayoutElement element={element} sectionId={sectionId} columnId={columnId} />;
      case 'spacer':
        return <SpacerElement element={element} />;
      case 'divider':
        return <DividerElement element={element} />;
      case 'icon':
        return <IconElement element={element} />;
      case 'carousel':
        return <CarouselElement element={element} />;
      case 'accordion':
        return <AccordionElement element={element} />;
      case 'tabs':
        return <TabsElement element={element} />;
      case 'contentBox':
        return <ContentBoxElement element={element} />;
      case 'customHtml':
        return <CustomHtmlElement element={element} />;
      default:
        return <div>Unknown element type</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      onClick={handleClick}
      className={`
        canvas-element relative transition-all animate-bounce-in group
        ${isPreviewMode 
          ? 'cursor-default' 
          : 'cursor-pointer'
        }
        ${!isPreviewMode && isSelected 
          ? 'element-selected ring-4 ring-blue-500 ring-offset-2 bg-blue-50 shadow-xl' 
          : !isPreviewMode 
            ? 'hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 hover:shadow-lg hover:bg-blue-50/50'
            : ''
        }
        ${isDragging ? 'opacity-50 scale-95' : ''}
        ${!isPreviewMode ? 'rounded-lg p-3 border-2 border-transparent hover:border-blue-200' : ''}
      `}
      style={styles as React.CSSProperties}
    >
      {/* Drag Handle - Only show in edit mode */}
      {!isPreviewMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-8 top-1/2 -translate-y-1/2 w-6 h-6 bg-gray-700 hover:bg-gray-900 rounded-md flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-all shadow-lg z-30"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      )}

      {/* Element Type Badge (visible when selected or on hover) - Only in edit mode */}
      {!isPreviewMode && (isSelected || !isDragging) && (
        <div className={`
          absolute -top-10 left-0 px-3 py-1.5 rounded-lg shadow-lg z-20 flex items-center gap-2
          transition-all duration-200 pointer-events-none
          ${isSelected 
            ? 'bg-blue-600 text-white opacity-100 scale-100' 
            : 'bg-gray-900 text-white opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100'
          }
        `}>
          <span className="text-xs font-bold uppercase tracking-wide">{element.type}</span>
          {!isSelected && <span className="text-xs">👆 Click to edit</span>}
          {isSelected && <span className="text-xs">✅ Selected</span>}
        </div>
      )}

      {/* Edit Indicator (only when selected) - Only in edit mode */}
      {!isPreviewMode && isSelected && (
        <div className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white rounded-md shadow-lg z-20 flex items-center gap-1 pointer-events-none">
          <span className="text-xs font-bold">✏️ EDITING</span>
        </div>
      )}

      {/* Click Overlay - Shows on hover to indicate clickability - Only in edit mode */}
      {!isPreviewMode && !isSelected && (
        <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-5 transition-all duration-200 rounded-lg pointer-events-none z-5" />
      )}

      {/* Element Content - Fully clickable */}
      <div className="relative z-10">
        {renderElement()}
      </div>
    </div>
  );
};

export default CanvasElement;

